import { type QueryClient, useQuery } from "@tanstack/react-query";
import { useAppAuth } from "@timeghost/auth/react/app-initializer";
import { toPermissionUser } from "@/lib/permissions/permission";
import type { PermissionUser } from "@/lib/permissions/types";
import stores from "@/lib/stores/web-api-stores";

export const currentUserQueryKey = ["current-user"] as const;

/** Keep RQ cache in sync after user PATCH/GET. Do not invalidate (refetch blinks the shell). */
export const syncCurrentUserQuery = (queryClient: QueryClient, user?: unknown): void => {
	const next = user ?? stores.user.useStore.getState().value;
	if (!next) {
		return;
	}
	queryClient.setQueryData(currentUserQueryKey, next);
};

export const useCurrentUser = () => {
	const { authMode } = useAppAuth();
	const userFromStore = stores.user.useStore((state) => state.value);
	const storeError = stores.user.useStore((state) => state.error);

	const query = useQuery({
		queryKey: currentUserQueryKey,
		queryFn: () => stores.user.actions.get(),
		enabled: authMode === "ready",
	});

	const permissionUser: PermissionUser | null = toPermissionUser(userFromStore ?? query.data);
	const hasUser = permissionUser !== null;
	const isInitialLoading = authMode === "ready" && !hasUser && (query.isPending || query.isLoading);

	return {
		user: permissionUser,
		// Prefer store so SignalR workspace/user patches show on already-loaded settings pages.
		rawUser: userFromStore ?? query.data,
		isInitialLoading,
		isFetching: query.isFetching,
		isLoading: isInitialLoading,
		isError: query.isError || (!!storeError && !hasUser),
		error: query.error ?? (storeError ? new Error(storeError) : null),
		refetch: query.refetch,
	};
};
